import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraCommandPalette, SpecraLogo, SpecraSearchTrigger } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Boxes, Crosshair, FileText, FolderOpen, LayoutDashboard, Library, Link2, MessageSquare, Moon, Settings, Shield, Sparkles, Sun, Target } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, matchPath, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { appPageMetadata } from '../apps/registry';
import { AssistRail } from '../components/AssistRail';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { BugReportButton } from '../components/BugReport';
import { NotificationBell } from '../components/notifications/NotificationBell';
import { ProjectSelector } from '../components/ProjectSelector';
import { ProposalProgressBar } from '../components/ProposalDashboard/ProposalProgressBar';
import { ProjectActionsMenu } from '../components/ProposalDashboard/v2/ProjectActionsMenu';
import { ProcessingJobsHost } from '../components/processing/ProcessingJobsHost';
import { ProcessingTray } from '../components/processing/ProcessingTray';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../contexts/authContext';
import { useDarkMode } from '../contexts/darkModeContext';
import { ProposalProgressProvider } from '../contexts/ProposalProgressContext';
import { useLayoutStore } from '../lib/stores/LayoutStore';
import { cn } from '../lib/utils';
// Roles that see the Admin console entry (mirrors AppSidebar / RequireAdminRoute).
const ADMIN_ROLES = new Set([
    'admin',
    'internal'
]);
// Define page metadata with pattern support
const pageMetadata = [
    {
        path: '/',
        title: 'Dashboard',
        description: 'AI-Powered Government Contracting Assistant'
    },
    {
        path: '/assistant',
        title: 'Assistant',
        description: 'Ask complex questions to your Federal GovCon AI partner'
    },
    {
        path: '/assistant/:id',
        title: 'Assistant',
        description: 'Converse with the AI'
    },
    {
        path: '/documents',
        title: 'Documents',
        description: 'Manage and organize your uploaded documents'
    },
    {
        path: '/sharepoint-documents',
        title: 'SharePoint Documents',
        description: 'Access SharePoint documents with automatic ACL filtering'
    },
    {
        path: '/delivery',
        title: 'Delivery',
        description: 'Programs, audits, and the rest of the delivery domain'
    },
    {
        path: '/delivery/org',
        title: 'Org Hierarchy',
        description: 'Sectors, business units, and portfolios'
    },
    {
        path: '/registries',
        title: 'Registries',
        description: 'Companies, contacts, government orgs, vehicles, and labor categories'
    },
    {
        path: '/delivery/programs',
        title: 'Programs',
        description: 'Active program records and everything tied to them'
    },
    {
        path: '/delivery/programs/:id',
        title: 'Program detail',
        description: 'Single program — overview, audits, risks, financial, etc.'
    },
    {
        path: '/delivery/audit-templates',
        title: 'Audit Templates',
        description: 'Reusable audit definitions and their question sets'
    },
    {
        path: '/history',
        title: 'History',
        description: 'Review your past queries and generated artifacts'
    },
    {
        path: '/library',
        title: 'Library',
        description: 'Access your collection of templates and boilerplate'
    },
    {
        path: '/vault',
        title: 'Vault',
        description: 'Securely store and manage your proposal documents'
    },
    {
        path: '/settings',
        title: 'Settings',
        description: 'Manage your account settings'
    },
    {
        path: '/help',
        title: 'Help',
        description: 'Find answers and get support'
    }
];
export default function DashboardLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { projectId, id } = useParams();
    const { user } = useAuth();
    const { isDarkMode, toggleDarkMode } = useDarkMode();
    const pageHeaderContent = useLayoutStore((state)=>state.pageHeaderContent);
    const showHeader = useLayoutStore((state)=>state.showHeader);
    const breadcrumbItems = useLayoutStore((state)=>state.breadcrumbItems);
    const assistOpen = useLayoutStore((state)=>state.assistOpen);
    const setAssistOpen = useLayoutStore((state)=>state.setAssistOpen);
    const setAssistOrigin = useLayoutStore((state)=>state.setAssistOrigin);
    const [paletteOpen, setPaletteOpen] = useState(false);
    // Open the app-wide Assist rail, remembering the route it was opened from so
    // "Open in full view" hands off to the right full-page assistant. Toggling
    // closed leaves the origin as-is (it's only read while open).
    const toggleAssist = useCallback(()=>{
        if (!assistOpen) setAssistOrigin(location.pathname);
        setAssistOpen(!assistOpen);
    }, [
        assistOpen,
        location.pathname,
        setAssistOpen,
        setAssistOrigin
    ]);
    // ⌘J / Ctrl+J toggles Assist app-wide (LAYOUT-SPEC §7).
    useEffect(()=>{
        const onKeyDown = (event)=>{
            if (event.key === 'j' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                toggleAssist();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return ()=>window.removeEventListener('keydown', onKeyDown);
    }, [
        toggleAssist
    ]);
    // Determine if we're in project mode (its own top strip carries the logo +
    // project selector, so the breadcrumb top bar is suppressed there).
    const isProjectRoute = location.pathname.startsWith('/project/') && projectId;
    const isProposalRoute = location.pathname.startsWith('/proposal/') && id;
    const isProjectMode = isProjectRoute || isProposalRoute;
    const isAdmin = !!user?.role && ADMIN_ROLES.has(user.role.toLowerCase());
    // Use matchPath to find matching metadata pattern
    const metadata = useMemo(()=>{
        // App packages first: they own their paths, and their patterns are more
        // specific than anything the shell's own table declares.
        for (const pattern of [
            ...appPageMetadata,
            ...pageMetadata
        ]){
            if (matchPath(pattern.path, location.pathname)) {
                return {
                    ...pattern,
                    description: pattern.description ?? ''
                };
            }
        }
        return {
            title: 'Dashboard',
            description: ''
        };
    }, [
        location.pathname
    ]);
    // Command palette destinations — the same nav surface as the sidebar, so ⌘K
    // reaches every top-level area without a backend search (LAYOUT-SPEC mockup
    // 2900). Records/entities land here once a real search service exists.
    const commandItems = useMemo(()=>{
        const nav = (idKey, label, href, icon, group)=>({
                id: idKey,
                label,
                group,
                icon,
                // These are navigation destinations, so the tile carries the one color
                // LAYOUT-SPEC §6 allows for reference (info/blue) — not a status color.
                // Record results (once search lands) will tint by their real status.
                tone: 'info',
                keywords: [
                    label
                ],
                onSelect: ()=>navigate(href)
            });
        const items = [
            nav('nav-dashboard', 'Dashboard', '/dashboard', /*#__PURE__*/ _jsx(LayoutDashboard, {}), 'Go to'),
            nav('nav-capture', 'Pursuits', '/capture', /*#__PURE__*/ _jsx(Crosshair, {}), 'Workflows'),
            nav('nav-delivery', 'Delivery', '/delivery', /*#__PURE__*/ _jsx(Boxes, {}), 'Workflows'),
            nav('nav-projects', 'Projects', '/projects', /*#__PURE__*/ _jsx(FolderOpen, {}), 'Workflows'),
            nav('nav-opportunities', 'Opportunities', '/opportunities', /*#__PURE__*/ _jsx(Target, {}), 'Workflows'),
            nav('nav-registries', 'Registries', '/registries', /*#__PURE__*/ _jsx(Library, {}), 'Go to'),
            nav('nav-chat', 'Chat', '/assistant', /*#__PURE__*/ _jsx(MessageSquare, {}), 'Tools'),
            nav('nav-documents', 'Documents', '/documents', /*#__PURE__*/ _jsx(FileText, {}), 'Tools'),
            nav('nav-sharepoint', 'SharePoint', '/sharepoint-documents', /*#__PURE__*/ _jsx(Link2, {}), 'Tools'),
            nav('nav-settings', 'Settings', '/settings', /*#__PURE__*/ _jsx(Settings, {}), 'Tools')
        ];
        if (isAdmin) {
            items.push(nav('nav-admin', 'Admin', '/admin', /*#__PURE__*/ _jsx(Shield, {}), 'Admin'));
        }
        return items;
    }, [
        isAdmin,
        navigate
    ]);
    const bodyHeader = showHeader && !isProjectMode ? /*#__PURE__*/ _jsx("div", {
        className: "border-b border-border bg-background px-8 pb-4 pt-5",
        children: pageHeaderContent ? pageHeaderContent : /*#__PURE__*/ _jsxs("div", {
            className: "min-w-0",
            children: [
                /*#__PURE__*/ _jsx("h1", {
                    className: "truncate text-xl font-semibold leading-tight text-foreground",
                    children: metadata.title
                }),
                metadata.description && /*#__PURE__*/ _jsx("p", {
                    className: "truncate text-sm leading-tight text-muted-foreground",
                    children: metadata.description
                })
            ]
        })
    }) : null;
    return /*#__PURE__*/ _jsx(ProposalProgressProvider, {
        children: /*#__PURE__*/ _jsxs("div", {
            className: "fixed inset-0 flex flex-col overflow-hidden bg-background",
            children: [
                isProjectMode && /*#__PURE__*/ _jsxs("div", {
                    className: "flex h-12 flex-shrink-0 items-center gap-3 px-4",
                    children: [
                        /*#__PURE__*/ _jsx(Link, {
                            className: "flex-shrink-0 transition-opacity hover:opacity-80",
                            to: "/dashboard",
                            children: /*#__PURE__*/ _jsx(SpecraLogo, {
                                className: "text-foreground",
                                size: 18,
                                variant: "mark"
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "h-4 w-px bg-border"
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "min-w-0 max-w-[min(60vw,40rem)]",
                            children: /*#__PURE__*/ _jsx(ProjectSelector, {
                                currentProjectId: projectId || id,
                                isCollapsed: false
                            })
                        }),
                        isProposalRoute && /*#__PURE__*/ _jsx(ProjectActionsMenu, {}),
                        /*#__PURE__*/ _jsx("div", {
                            className: "flex-1"
                        }),
                        /*#__PURE__*/ _jsx(BugReportButton, {}),
                        /*#__PURE__*/ _jsx(NotificationBell, {})
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex min-h-0 flex-1",
                    children: [
                        /*#__PURE__*/ _jsx(Sidebar, {}),
                        /*#__PURE__*/ _jsxs("main", {
                            className: "flex min-w-0 flex-1 flex-col overflow-hidden rounded-tl-xl bg-card",
                            children: [
                                !isProjectMode && /*#__PURE__*/ _jsxs("header", {
                                    className: "flex h-[var(--component-topbar-height)] flex-shrink-0 items-center gap-4 border-b border-border bg-background px-6",
                                    children: [
                                        /*#__PURE__*/ _jsx(Breadcrumbs, {
                                            className: "min-w-0 flex-1",
                                            items: breadcrumbItems ?? undefined
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraSearchTrigger, {
                                            className: "hidden sm:flex",
                                            onClick: ()=>setPaletteOpen(true)
                                        }),
                                        /*#__PURE__*/ _jsxs("button", {
                                            "aria-pressed": assistOpen,
                                            className: cn('hidden items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11.5px] font-medium transition-colors sm:inline-flex', assistOpen ? 'border-border-strong bg-muted text-foreground' : 'border-border text-muted-foreground hover:border-border-strong hover:text-foreground'),
                                            onClick: toggleAssist,
                                            title: "Assist (⌘J)",
                                            type: "button",
                                            children: [
                                                /*#__PURE__*/ _jsx(Sparkles, {
                                                    "aria-hidden": "true",
                                                    className: "size-3"
                                                }),
                                                "Assist"
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("button", {
                                            "aria-label": isDarkMode ? 'Switch to light mode' : 'Switch to dark mode',
                                            className: "flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
                                            "data-testid": Testids.appNav.themeToggle,
                                            onClick: toggleDarkMode,
                                            title: isDarkMode ? 'Switch to light mode' : 'Switch to dark mode',
                                            type: "button",
                                            children: isDarkMode ? /*#__PURE__*/ _jsx(Sun, {
                                                "aria-hidden": "true",
                                                className: "size-4"
                                            }) : /*#__PURE__*/ _jsx(Moon, {
                                                "aria-hidden": "true",
                                                className: "size-4"
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(BugReportButton, {}),
                                        /*#__PURE__*/ _jsx(NotificationBell, {})
                                    ]
                                }),
                                bodyHeader,
                                /*#__PURE__*/ _jsx(ProposalProgressBar, {}),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "flex min-h-0 flex-1 flex-col overflow-auto",
                                    children: /*#__PURE__*/ _jsx(Outlet, {})
                                })
                            ]
                        }),
                        assistOpen ? /*#__PURE__*/ _jsx(AssistRail, {
                            onClose: ()=>setAssistOpen(false)
                        }) : null
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraCommandPalette, {
                    items: commandItems,
                    onOpenChange: setPaletteOpen,
                    open: paletteOpen,
                    placeholder: "Search areas and actions…"
                }),
                /*#__PURE__*/ _jsx(ProcessingJobsHost, {}),
                /*#__PURE__*/ _jsx(ProcessingTray, {})
            ]
        })
    });
}
